import { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import { useTranslation } from "@/i18n";
import { Input, type InputProps } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface PasswordInputProps extends Omit<InputProps, "type"> {
  /** Initial visibility state. Defaults to false (hidden). */
  defaultVisible?: boolean;
}

/**
 * Password input with a built-in show/hide toggle. The toggle
 * lives inside the input shell as an absolutely-positioned eye
 * icon button — keeps the field width identical to a plain
 * ``<Input>`` so callers can drop it in wherever an obscured
 * value is expected.
 *
 * Used directly by ``FormField`` when ``type="password"`` is
 * passed, so every login / register / BYOK / rotate form
 * inherits the toggle without a per-call-site migration. Can
 * also be used standalone for callers that don't need the
 * FormField label / hint / error shell.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ defaultVisible = false, className, ...inputProps }, ref) => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(defaultVisible);

    return (
      <div className="relative">
        <Input
          {...inputProps}
          ref={ref}
          type={visible ? "text" : "password"}
          // Reserve right padding so the typed text never slides
          // under the toggle button.
          className={cn("pr-10", className)}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          disabled={inputProps.disabled}
          aria-label={
            visible
              ? t("shared.passwordInput.hide")
              : t("shared.passwordInput.show")
          }
          aria-pressed={visible}
          className={cn(
            "absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1.5",
            "text-neutral-500 transition-colors",
            "hover:bg-ocean-50 hover:text-ocean-700",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400",
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          )}
          tabIndex={inputProps.disabled ? -1 : 0}
        >
          {visible ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = "PasswordInput";
